/**
 * AIOP SSE Client
 *
 * Real-time sync for AllInOnePopup via Server-Sent Events.
 * Replaces the WebSocket client — same public interface (acquire/release/onUpdate/connectionId)
 * so useAIOPWebSocket.js needs no changes.
 *
 * SSE works through AWS App Runner and any HTTP/HTTPS proxy.
 *
 * This is the tab's only server-sent-events connection. The FrontDesk refresh
 * notifications (frontDeskWebSocketClient) ride on it too, via subscribeFrontDesk():
 * each open stream counts toward App Runner's per-instance concurrency, so a tab
 * must not hold a second one. The stream needs a login, so it is opened with a
 * short-lived ticket from POST /stream-ticket.
 */

import axios from 'axios';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://vwwjda2e99.us-west-2.awsapprunner.com';

const SSE_URL = `${API_BASE_URL}/aiop-stream`;
const TICKET_URL = `${API_BASE_URL}/stream-ticket`;

// Close a FrontDesk-only stream once the tab has been hidden this long; reopen (and
// refresh once, since events were missed) when it's visible again.
const HIDDEN_CLOSE_MS = 5 * 60 * 1000;
// While logged out there is no ticket to fetch; look for a session again this often.
const LOGIN_RECHECK_MS = 15 * 1000;

const FRONTDESK_EVENT_TYPES = new Set(['refresh', 'immediate-refresh']);

// Resolves to { ticket } on success, { unsupported: true } when the backend predates
// stream tickets (its /aiop-stream still accepts ticketless connections), or {} when no
// ticket can be had right now (logged out, session expired, backend unreachable).
async function fetchStreamTicket() {
  const token = localStorage.getItem('sessionToken');
  if (!token) return {};

  const headers = { Authorization: `Bearer ${token}` };
  const idToken = localStorage.getItem('cognitoIdToken');
  if (idToken) headers['x-id-token'] = idToken;

  try {
    const response = await axios.post(TICKET_URL, {}, { headers });
    return response.data?.ticket ? { ticket: response.data.ticket } : {};
  } catch (error) {
    return error?.response?.status === 404 ? { unsupported: true } : {};
  }
}

class AIOPSSEClient {
  constructor() {
    this.source = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectDelay = 3000;
    this.callbacks = [];
    this.refCount = 0;

    // Unique ID assigned by the server on first connect.
    // Sent with every axios request as X-Connection-Id for echo-skip.
    this.connectionId = null;

    // FrontDesk subscribers sharing this connection.
    this.frontDeskSubscribers = new Set();
    this.isFrontDeskSubscribed = false; // the open stream was opened with a valid ticket
    this.sourceHasTicket = false;
    this.isOpening = false;
    this.openGeneration = 0;
    this.loginRecheckTimer = null;
    this.hiddenTimer = null;
    this.closedWhileHidden = false;

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => this._handleVisibilityChange());
    }
  }

  acquire() {
    this.refCount++;
    if (this.refCount === 1) {
      this.connect();
    }
  }

  release() {
    this.refCount = Math.max(0, this.refCount - 1);
    if (this.refCount === 0 && this.frontDeskSubscribers.size === 0) {
      this.disconnect();
    }
  }

  _isNeeded() {
    if (this.refCount > 0) return true;
    // A FrontDesk-only stream stays closed while the tab is hidden; it reopens on return.
    return this.frontDeskSubscribers.size > 0 && !this.closedWhileHidden;
  }

  connect() {
    if (this.isConnected || this.source || this.isOpening) return;

    this.isOpening = true;
    const generation = ++this.openGeneration;
    fetchStreamTicket().then(({ ticket, unsupported }) => {
      if (generation !== this.openGeneration) return; // superseded by disconnect/connect
      this.isOpening = false;
      if (!this._isNeeded() || this.source) return;

      if (ticket || unsupported) {
        this._open(ticket || null);
        return;
      }

      // No ticket (logged out, session expired or backend unreachable): try again later.
      if (this.frontDeskSubscribers.size > 0) this._scheduleLoginRecheck();
      if (this.refCount > 0) this._attemptReconnect();
    });
  }

  _open(ticket) {
    try {
      const url = ticket ? `${SSE_URL}?ticket=${encodeURIComponent(ticket)}` : SSE_URL;
      this.source = new EventSource(url);
      this.sourceHasTicket = Boolean(ticket);

      this.source.onopen = () => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
      };

      this.source.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // First message from server assigns our connectionId
          if (data.type === 'connected') {
            this.connectionId = data.connectionId;
            axios.defaults.headers.common['X-Connection-Id'] = this.connectionId;
            return;
          }

          if (data.type === 'frontdesk-subscribed') {
            this._setFrontDeskSubscribed(true);
            return;
          }

          if (FRONTDESK_EVENT_TYPES.has(data.type)) {
            this._emitFrontDesk('onEvent', data);
            return;
          }

          if (data.type !== 'aiop-update') return;

          this.callbacks.forEach((cb) => {
            try { cb(data); } catch (err) {
              console.error('[AIOP-SSE] Callback error:', err);
            }
          });
        } catch {
          // ignore malformed messages
        }
      };

      this.source.onerror = () => {
        this.isConnected = false;
        this.source?.close();
        this.source = null;
        this.sourceHasTicket = false;
        this._setFrontDeskSubscribed(false);
        this._attemptReconnect();
      };
    } catch (err) {
      console.error('[AIOP-SSE] Connection error:', err);
      this._attemptReconnect();
    }
  }

  disconnect() {
    this.reconnectAttempts = this.maxReconnectAttempts; // prevent reconnect
    this._closeSource();
  }

  _closeSource() {
    this.openGeneration++;
    this.isOpening = false;
    if (this.source) {
      this.source.close();
      this.source = null;
    }
    this.sourceHasTicket = false;
    this.isConnected = false;
    this._setFrontDeskSubscribed(false);
  }

  _attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts || !this._isNeeded()) return;
    this.reconnectAttempts++;

    const delay = this.reconnectDelay * Math.min(this.reconnectAttempts, 3);
    setTimeout(() => {
      if (!this.isConnected && !this.source && this._isNeeded()) {
        this.connect();
      }
    }, delay);
  }

  onUpdate(callback) {
    this.callbacks.push(callback);
    return () => {
      const idx = this.callbacks.indexOf(callback);
      if (idx > -1) this.callbacks.splice(idx, 1);
    };
  }

  /**
   * Receive FrontDesk refresh notifications on this connection.
   * @param {{onEvent: Function, onConnect?: Function, onDisconnect?: Function}} subscriber
   * @returns {Function} unsubscribe
   */
  subscribeFrontDesk(subscriber) {
    this.frontDeskSubscribers.add(subscriber);

    if (this.frontDeskSubscribers.size === 1) {
      this.reconnectAttempts = 0;
      if (this.source && !this.sourceHasTicket) {
        // A ticketless stream (older backend) is open; reopen it in case a ticket can be had now.
        this._closeSource();
        this.connect();
      } else if (!this.source) {
        this.connect();
      } else if (this.isFrontDeskSubscribed) {
        // An AIOP popup already opened a ticketed stream.
        subscriber.onConnect?.();
      }
    } else if (this.isFrontDeskSubscribed) {
      subscriber.onConnect?.();
    }

    return () => {
      if (!this.frontDeskSubscribers.delete(subscriber)) return;
      if (this.frontDeskSubscribers.size === 0) {
        clearTimeout(this.loginRecheckTimer);
        this.loginRecheckTimer = null;
        if (this.refCount === 0) this.disconnect();
      }
    };
  }

  _setFrontDeskSubscribed(subscribed) {
    if (this.isFrontDeskSubscribed === subscribed) return;
    this.isFrontDeskSubscribed = subscribed;
    this._emitFrontDesk(subscribed ? 'onConnect' : 'onDisconnect');
  }

  _emitFrontDesk(handler, data) {
    [...this.frontDeskSubscribers].forEach((subscriber) => {
      try { subscriber[handler]?.(data); } catch (err) {
        console.error('[AIOP-SSE] FrontDesk callback error:', err);
      }
    });
  }

  _scheduleLoginRecheck() {
    if (this.loginRecheckTimer) return;
    this.loginRecheckTimer = setTimeout(() => {
      this.loginRecheckTimer = null;
      if (this.frontDeskSubscribers.size === 0) return;
      if (this.source && !this.sourceHasTicket) this._closeSource();
      if (!this.source) this.connect();
    }, LOGIN_RECHECK_MS);
  }

  _handleVisibilityChange() {
    if (document.hidden) {
      if (this.hiddenTimer || this.frontDeskSubscribers.size === 0) return;
      this.hiddenTimer = setTimeout(() => {
        this.hiddenTimer = null;
        // Only close a FrontDesk-only stream; open AIOP popups keep live sync.
        // Also covers a stream mid-reconnect (ticket fetch in flight, or a retry pending).
        if (!document.hidden || this.refCount > 0) return;
        this.closedWhileHidden = true;
        this._closeSource();
      }, HIDDEN_CLOSE_MS);
      return;
    }

    clearTimeout(this.hiddenTimer);
    this.hiddenTimer = null;
    if (!this.closedWhileHidden) return;
    this.closedWhileHidden = false;
    if (this.frontDeskSubscribers.size === 0) return;

    this.reconnectAttempts = 0;
    this.connect();
    // Changes made while the stream was closed were never pushed; refresh once.
    this._emitFrontDesk('onEvent', {
      type: 'refresh',
      sources: ['ordered', 'shopify'],
      timestamp: new Date().toISOString(),
    });
  }
}

// Singleton — shared across all open AIOP popups and the FrontDesk notifications
const aiopWebSocket = new AIOPSSEClient();
export default aiopWebSocket;
